import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { FaArrowLeft, FaUtensils, FaClock, FaHistory, FaHeart, FaMagic, FaSpinner, FaLeaf, FaFire, FaStar, FaTrash, FaDumbbell, FaBed } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Iconos y colores por tipo de dieta
const DIET_CONFIG = {
  training_inferior: { icon: FaDumbbell, color: '#FF6B35', label: 'Tren Inferior' },
  training_superior: { icon: FaDumbbell, color: '#00D4AA', label: 'Tren Superior' },
  descanso: { icon: FaBed, color: '#8B5CF6', label: 'Descanso' },
  rest: { icon: FaBed, color: '#8B5CF6', label: 'Descanso' },
  lineal: { icon: FaUtensils, color: 'var(--brand-primary)', label: 'Dieta Lineal' }
};

const RecipeIdeas = ({ user }) => {
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [loadingDiets, setLoadingDiets] = useState(true);
  const [diets, setDiets] = useState([]);
  const [activeDietTab, setActiveDietTab] = useState('');
  const [preferences, setPreferences] = useState('');
  const [generatedRecipes, setGeneratedRecipes] = useState(null);
  const [recipeHistory, setRecipeHistory] = useState([]);
  const [savedRecipes, setSavedRecipes] = useState([]);
  const [activeTab, setActiveTab] = useState('generate');
  const [selectedMeal, setSelectedMeal] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);
  const [generatingFor, setGeneratingFor] = useState(null);
  const [savingRecipe, setSavingRecipe] = useState(false);

  useEffect(() => {
    if (user?.email) {
      fetchStructuredDiets();
      fetchHistory();
      fetchSavedRecipes();
    }
  }, [user?.email]);

  const fetchStructuredDiets = async () => {
    try {
      setLoadingDiets(true);
      const response = await axios.get(`${API}/recipes/structured-meals/${encodeURIComponent(user.email)}`);
      if (response.data.has_diets) {
        setDiets(response.data.diets);
        // Set first diet as active
        if (response.data.diets.length > 0) {
          setActiveDietTab(response.data.diets[0].diet_type);
        }
      }
    } catch (error) {
      console.error('Error fetching structured diets:', error);
    } finally {
      setLoadingDiets(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/recipes/history/${encodeURIComponent(user.email)}?limit=10`);
      setRecipeHistory(response.data.recipes || []);
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  const fetchSavedRecipes = async () => {
    try {
      const response = await axios.get(`${API}/recipes/saved/${encodeURIComponent(user.email)}`);
      setSavedRecipes(response.data.recipes || []);
    } catch (error) {
      console.error('Error fetching saved recipes:', error);
    }
  };

  const generateRecipesForOption = async (dietType, mealName, optionNumber, ingredients) => {
    setLoading(true);
    setGeneratingFor({ diet: dietType, meal: mealName, option: optionNumber });
    
    try {
      const response = await axios.post(`${API}/recipes/generate-for-option`, null, {
        params: {
          email: user.email,
          meal_name: mealName,
          option_number: optionNumber,
          diet_type: dietType,
          preferences: preferences || null
        }
      });

      const dietLabel = diets.find(d => d.diet_type === dietType)?.diet_label || dietType;
      setGeneratedRecipes({
        ...response.data,
        displayTitle: `${dietLabel} - ${mealName} - Opción ${optionNumber}`
      });
      setSelectedMeal(mealName);
      setSelectedOption(optionNumber);
      toast.success(`¡3 recetas generadas para ${mealName}!`);
      fetchHistory();
    } catch (error) {
      console.error('Error generating recipes:', error);
      toast.error(error.response?.data?.detail || 'Error al generar recetas');
    } finally {
      setLoading(false);
      setGeneratingFor(null);
    }
  };

  const saveCurrentRecipe = async () => {
    if (!generatedRecipes) return;
    
    setSavingRecipe(true);
    try {
      const recipeData = {
        recipe_name: generatedRecipes.displayTitle || `Recetas de ${generatedRecipes.meal_name}`,
        ingredients: generatedRecipes.ingredients || [],
        instructions: generatedRecipes.recipes_text,
        meal_type: generatedRecipes.meal_name || 'General'
      };

      const response = await axios.post(`${API}/recipes/save`, recipeData, {
        params: { email: user.email }
      });

      if (response.data.success) {
        toast.success('¡Recetas guardadas en favoritos!');
        fetchSavedRecipes();
      }
    } catch (error) {
      console.error('Error saving recipe:', error);
      toast.error('Error al guardar las recetas');
    } finally {
      setSavingRecipe(false);
    }
  };

  const deleteSavedRecipe = async (recipeId) => {
    try {
      const response = await axios.delete(`${API}/recipes/saved/${recipeId}`);
      if (response.data.success) {
        toast.success('Receta eliminada de favoritos');
        fetchSavedRecipes();
      }
    } catch (error) {
      console.error('Error deleting recipe:', error);
      toast.error('Error al eliminar la receta');
    }
  };

  const getDietIcon = (dietType) => {
    const config = DIET_CONFIG[dietType] || DIET_CONFIG.lineal;
    const IconComponent = config.icon;
    return <IconComponent style={{ color: config.color }} />;
  };

  const getDietColor = (dietType) => {
    return DIET_CONFIG[dietType]?.color || 'var(--brand-primary)';
  };

  return (
    <div 
      className="min-h-screen py-8 px-4 md:px-6 relative"
      style={{ backgroundColor: '#0a0a0a' }}
      data-testid="recipe-ideas-page"
    >
      {/* Mesh gradient background */}
      <div 
        className="fixed inset-0 z-0 pointer-events-none"
        style={{
          background: 'radial-gradient(circle at 30% 70%, rgba(var(--brand-primary-glow), 0.1), transparent 50%), radial-gradient(circle at 70% 30%, rgba(0, 240, 255, 0.08), transparent 40%)'
        }}
      />
      
      <div className="relative z-10 max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/dashboard')} 
            className="text-gray-400 hover:text-white"
            data-testid="back-to-dashboard"
          >
            <FaArrowLeft className="mr-2" /> Volver
          </Button>
        </div>

        {/* Título */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-10">
          <p 
            className="text-xs font-bold uppercase tracking-[0.3em] mb-3"
            style={{ color: 'var(--brand-primary)' }}
          >
            Ideas de preparación
          </p>
          <h1 
            className="text-4xl md:text-5xl font-bold text-white mb-4 uppercase tracking-tight"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            Recetas con tu Plan
          </h1>
          <p className="text-gray-400">
            Selecciona tu tipo de dieta, comida y opción para obtener <span className="text-cyan-400 font-semibold">3 formas diferentes</span> de preparar tus alimentos
          </p>
        </motion.div>

        {/* Main Tabs */}
        <div className="flex flex-wrap gap-3 mb-8">
          <Button
            onClick={() => setActiveTab('generate')}
            className={`rounded-full px-6 font-bold uppercase tracking-wider ${
              activeTab === 'generate' 
                ? 'text-white' 
                : 'bg-transparent text-gray-400 hover:text-white border border-gray-700'
            }`}
            style={activeTab === 'generate' ? { background: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)' } : {}}
            data-testid="tab-generate"
          >
            <FaUtensils className="mr-2" /> Generar
          </Button>
          <Button
            onClick={() => setActiveTab('favorites')}
            className={`rounded-full px-6 font-bold uppercase tracking-wider ${
              activeTab === 'favorites' 
                ? 'text-white' 
                : 'bg-transparent text-gray-400 hover:text-white border border-gray-700'
            }`}
            style={activeTab === 'favorites' ? { background: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)' } : {}}
            data-testid="tab-favorites"
          >
            <FaStar className="mr-2" /> Favoritos ({savedRecipes.length})
          </Button>
          <Button
            onClick={() => setActiveTab('history')}
            className={`rounded-full px-6 font-bold uppercase tracking-wider ${
              activeTab === 'history' 
                ? 'text-white' 
                : 'bg-transparent text-gray-400 hover:text-white border border-gray-700'
            }`}
            style={activeTab === 'history' ? { background: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)' } : {}}
            data-testid="tab-history"
          >
            <FaHistory className="mr-2" /> Historial ({recipeHistory.length})
          </Button>
        </div>

        <AnimatePresence mode="wait">
          {activeTab === 'generate' ? (
            <motion.div
              key="generate"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="grid grid-cols-1 lg:grid-cols-3 gap-8"
            >
              {/* Panel de dietas y comidas */}
              <div className="lg:col-span-1 space-y-6">
                {/* Preferencias */}
                <div 
                  className="rounded-2xl p-6 backdrop-blur-xl"
                  style={{ 
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.1)'
                  }}
                >
                  <h3 className="text-lg font-bold text-white mb-4 uppercase" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    Preferencias (opcional)
                  </h3>
                  <Textarea
                    placeholder="Ej: prefiero salteados, sin salsas pesadas, más condimentos picantes..."
                    value={preferences}
                    onChange={(e) => setPreferences(e.target.value)}
                    className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 resize-none"
                    rows={2}
                    data-testid="preferences-input"
                  />
                </div>

                {/* Dietas y comidas */}
                <div 
                  className="rounded-2xl p-6 backdrop-blur-xl"
                  style={{ 
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.1)'
                  }}
                >
                  <div className="flex items-center gap-2 mb-4">
                    <FaLeaf style={{ color: '#00F0FF' }} />
                    <h3 className="text-lg font-bold text-white uppercase" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      Tus Dietas
                    </h3>
                  </div>
                  
                  {loadingDiets ? (
                    <div className="flex items-center justify-center py-8">
                      <FaSpinner className="animate-spin text-2xl" style={{ color: 'var(--brand-primary)' }} />
                    </div>
                  ) : diets.length > 0 ? (
                    <div className="space-y-4">
                      {/* Diet Tabs */}
                      {diets.length > 1 && (
                        <div className="flex flex-wrap gap-2 mb-4">
                          {diets.map((diet) => (
                            <Button
                              key={diet.diet_type}
                              size="sm"
                              onClick={() => setActiveDietTab(diet.diet_type)}
                              className={`rounded-full text-xs px-3 py-1 h-auto flex items-center gap-1 ${
                                activeDietTab === diet.diet_type
                                  ? 'text-white'
                                  : 'bg-transparent text-gray-400 border border-gray-600'
                              }`}
                              style={activeDietTab === diet.diet_type ? { background: getDietColor(diet.diet_type) } : {}}
                            >
                              {getDietIcon(diet.diet_type)}
                              <span>{diet.diet_label}</span>
                            </Button>
                          ))}
                        </div>
                      )}
                      
                      {/* Active Diet Meals */}
                      {diets.filter(d => d.diet_type === activeDietTab || diets.length === 1).map((diet) => (
                        <div key={diet.diet_type}>
                          {diets.length === 1 && (
                            <div className="flex items-center gap-2 mb-3 pb-2 border-b border-white/10">
                              {getDietIcon(diet.diet_type)}
                              <span className="text-white font-semibold">{diet.diet_label}</span>
                              <Badge className="text-xs bg-white/10">{diet.meals_count} comidas</Badge>
                            </div>
                          )}
                          
                          <Accordion type="single" collapsible className="space-y-2">
                            {diet.meals.map((meal, mealIdx) => (
                              <AccordionItem 
                                key={mealIdx} 
                                value={`meal-${mealIdx}`}
                                className="border-0"
                              >
                                <AccordionTrigger 
                                  className="px-4 py-3 rounded-xl hover:no-underline text-left"
                                  style={{ 
                                    background: `rgba(${parseInt(getDietColor(diet.diet_type).slice(1,3), 16)}, ${parseInt(getDietColor(diet.diet_type).slice(3,5), 16)}, ${parseInt(getDietColor(diet.diet_type).slice(5,7), 16)}, 0.15)`,
                                    border: `1px solid ${getDietColor(diet.diet_type)}40`
                                  }}
                                >
                                  <div className="flex items-center gap-3">
                                    <span className="text-white font-semibold">Comida {mealIdx + 1}</span>
                                    {meal.meal_time && (
                                      <Badge className="text-xs" style={{ background: 'rgba(0, 240, 255, 0.2)' }}>
                                        {meal.meal_time}
                                      </Badge>
                                    )}
                                    <Badge className="text-xs bg-white/10">
                                      {meal.options.length} {meal.options.length === 1 ? 'opción' : 'opciones'}
                                    </Badge>
                                  </div>
                                </AccordionTrigger>
                                <AccordionContent className="pt-2 px-2">
                                  <div className="space-y-3">
                                    {meal.options.map((option, optIdx) => (
                                      <div 
                                        key={optIdx}
                                        className="p-4 rounded-xl"
                                        style={{ 
                                          background: 'rgba(255, 255, 255, 0.03)',
                                          border: selectedMeal === meal.meal_name && selectedOption === option.option_number
                                            ? '2px solid #00F0FF'
                                            : '1px solid rgba(255, 255, 255, 0.1)'
                                        }}
                                      >
                                        <div className="flex justify-between items-start mb-3">
                                          <span className="text-cyan-400 font-semibold text-sm">
                                            Opción {option.option_number}
                                          </span>
                                          <Button
                                            size="sm"
                                            onClick={() => generateRecipesForOption(diet.diet_type, meal.meal_name, option.option_number, option.ingredients)}
                                            disabled={loading}
                                            className="rounded-full text-xs px-4 py-1 h-auto"
                                            style={{ background: getDietColor(diet.diet_type) }}
                                            data-testid={`generate-btn-${diet.diet_type}-${mealIdx}-${optIdx}`}
                                          >
                                            {generatingFor?.diet === diet.diet_type && generatingFor?.meal === meal.meal_name && generatingFor?.option === option.option_number ? (
                                              <FaSpinner className="animate-spin mr-1" />
                                            ) : (
                                              <FaMagic className="mr-1" />
                                            )}
                                            Generar
                                          </Button>
                                        </div>
                                        <div className="flex flex-wrap gap-1">
                                          {option.ingredients.map((ing, ingIdx) => (
                                            <Badge 
                                              key={ingIdx}
                                              className="text-xs text-gray-300 border-0"
                                              style={{ background: 'rgba(255, 255, 255, 0.1)' }}
                                            >
                                              {ing}
                                            </Badge>
                                          ))}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </AccordionContent>
                              </AccordionItem>
                            ))}
                          </Accordion>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <p className="text-gray-500 text-sm">No se encontraron dietas en tu plan.</p>
                      <p className="text-gray-600 text-xs mt-2">Asegúrate de tener un plan de dieta asignado.</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Panel de resultados */}
              <div className="lg:col-span-2">
                {generatedRecipes ? (
                  <div 
                    className="rounded-2xl backdrop-blur-xl overflow-hidden"
                    style={{ 
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(0, 240, 255, 0.3)'
                    }}
                  >
                    <div 
                      className="p-6 flex justify-between items-start"
                      style={{ background: 'linear-gradient(135deg, rgba(0, 240, 255, 0.1), rgba(var(--brand-primary-glow), 0.1))' }}
                    >
                      <div>
                        <h3 className="text-xl font-bold text-white flex items-center gap-2 uppercase" style={{ fontFamily: 'Poppins, sans-serif' }}>
                          <FaUtensils style={{ color: '#00F0FF' }} /> 
                          {generatedRecipes.displayTitle || 'Tus Recetas'}
                        </h3>
                        <p className="text-gray-400 text-sm mt-1">
                          3 formas de preparar: {generatedRecipes.ingredients?.join(', ')}
                        </p>
                        <p className="text-gray-500 text-xs mt-2">
                          Generadas el {new Date(generatedRecipes.generated_at).toLocaleDateString('es-ES', {
                            day: 'numeric',
                            month: 'long',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                      <Button
                        onClick={saveCurrentRecipe}
                        disabled={savingRecipe}
                        className="rounded-full px-4 text-sm"
                        style={{ background: 'linear-gradient(135deg, #FFD700, #FFA500)' }}
                        data-testid="save-recipe-btn"
                      >
                        {savingRecipe ? (
                          <FaSpinner className="animate-spin mr-2" />
                        ) : (
                          <FaStar className="mr-2" />
                        )}
                        Guardar
                      </Button>
                    </div>
                    <div className="p-6">
                      <div className="prose prose-invert max-w-none" data-testid="generated-recipes">
                        <ReactMarkdown
                          components={{
                            h3: ({ children }) => (
                              <h3 className="text-xl font-bold mt-6 mb-3 pb-2 flex items-center gap-2" style={{ color: 'var(--brand-primary)', borderBottom: '2px solid rgba(var(--brand-primary-glow), 0.3)' }}>
                                {children}
                              </h3>
                            ),
                            strong: ({ children }) => (
                              <strong className="text-cyan-400 font-semibold">{children}</strong>
                            ),
                            li: ({ children }) => (
                              <li className="text-gray-300 my-1">{children}</li>
                            ),
                            p: ({ children }) => (
                              <p className="text-gray-300 my-2">{children}</p>
                            ),
                            hr: () => (
                              <hr className="my-6 border-white/10" />
                            )
                          }}
                        >
                          {generatedRecipes.recipes_text}
                        </ReactMarkdown>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div 
                    className="rounded-2xl p-12 backdrop-blur-xl text-center"
                    style={{ 
                      background: 'rgba(255, 255, 255, 0.03)',
                      border: '2px dashed rgba(var(--brand-primary-glow), 0.3)'
                    }}
                  >
                    <div 
                      className="w-24 h-24 mx-auto mb-6 rounded-full flex items-center justify-center"
                      style={{ background: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)' }}
                    >
                      <FaUtensils className="text-white text-4xl" />
                    </div>
                    <h3 
                      className="text-2xl font-bold text-white mb-2 uppercase"
                      style={{ fontFamily: 'Poppins, sans-serif' }}
                    >
                      ¿Lista para cocinar?
                    </h3>
                    <p className="text-gray-400 max-w-md mx-auto mb-6">
                      {diets.length > 1 
                        ? 'Selecciona tu tipo de dieta, comida y opción del panel izquierdo.'
                        : 'Selecciona una comida y opción del panel izquierdo.'}
                      {' '}Te daremos <span className="text-cyan-400 font-semibold">3 formas diferentes</span> de preparar 
                      exactamente esos ingredientes.
                    </p>
                    <div className="flex flex-wrap gap-4 justify-center text-sm text-gray-500">
                      <div className="flex items-center gap-2">
                        <FaClock style={{ color: 'var(--brand-primary)' }} /> Recetas rápidas (-30 min)
                      </div>
                      <div className="flex items-center gap-2">
                        <FaFire style={{ color: '#F59E0B' }} /> Tips de condimentos
                      </div>
                      <div className="flex items-center gap-2">
                        <FaHeart style={{ color: '#00F0FF' }} /> Fitness-friendly
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          ) : activeTab === 'favorites' ? (
            <motion.div
              key="favorites"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <div className="space-y-6">
                {savedRecipes.length > 0 ? (
                  savedRecipes.map((item, idx) => (
                    <div 
                      key={item.id || idx} 
                      className="rounded-2xl backdrop-blur-xl overflow-hidden"
                      style={{ 
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid rgba(255, 215, 0, 0.3)'
                      }}
                    >
                      <div 
                        className="p-6 border-b flex justify-between items-start" 
                        style={{ 
                          borderColor: 'rgba(255, 255, 255, 0.1)',
                          background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.1), rgba(255, 165, 0, 0.05))'
                        }}
                      >
                        <div>
                          <h3 className="text-lg font-bold text-white flex items-center gap-2">
                            <FaStar style={{ color: '#FFD700' }} />
                            {item.recipe_name}
                          </h3>
                          <p className="text-sm text-gray-400 flex items-center gap-2 mt-1">
                            <FaClock style={{ color: '#00F0FF' }} />
                            Guardada el {new Date(item.saved_at).toLocaleDateString('es-ES', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric'
                            })}
                          </p>
                          {item.ingredients?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {item.ingredients.slice(0, 5).map((ing, i) => (
                                <Badge key={i} className="text-xs bg-white/10">{ing}</Badge>
                              ))}
                              {item.ingredients.length > 5 && (
                                <Badge className="text-xs bg-white/5">+{item.ingredients.length - 5} más</Badge>
                              )}
                            </div>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteSavedRecipe(item.id)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          data-testid={`delete-saved-${idx}`}
                        >
                          <FaTrash />
                        </Button>
                      </div>
                      <div className="p-6">
                        <div className="prose prose-invert prose-sm max-w-none">
                          <ReactMarkdown
                            components={{
                              h3: ({ children }) => (
                                <h3 className="text-lg font-bold mt-4 mb-2" style={{ color: 'var(--brand-primary)' }}>
                                  {children}
                                </h3>
                              ),
                              strong: ({ children }) => (
                                <strong className="text-cyan-400">{children}</strong>
                              )
                            }}
                          >
                            {item.instructions}
                          </ReactMarkdown>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div 
                    className="rounded-2xl p-12 text-center"
                    style={{ 
                      background: 'rgba(255, 255, 255, 0.03)',
                      border: '2px dashed rgba(255, 215, 0, 0.3)'
                    }}
                  >
                    <div 
                      className="w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center"
                      style={{ background: 'linear-gradient(135deg, #FFD700, #FFA500)' }}
                    >
                      <FaStar className="text-white text-3xl" />
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-2">
                      Sin recetas favoritas
                    </h3>
                    <p className="text-gray-500 mb-6">
                      Genera recetas y guárdalas aquí para acceder rápidamente
                    </p>
                    <Button
                      onClick={() => setActiveTab('generate')}
                      className="rounded-full px-8 font-bold uppercase tracking-wider text-white"
                      style={{ background: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)' }}
                    >
                      Generar Recetas
                    </Button>
                  </div>
                )}
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="history"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <div className="space-y-6">
                {recipeHistory.length > 0 ? (
                  recipeHistory.map((item, idx) => (
                    <div 
                      key={item.id || idx} 
                      className="rounded-2xl backdrop-blur-xl overflow-hidden"
                      style={{ 
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid rgba(255, 255, 255, 0.1)'
                      }}
                    >
                      <div className="p-6 border-b" style={{ borderColor: 'rgba(255, 255, 255, 0.1)' }}>
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="text-lg font-bold text-white flex items-center gap-2">
                              {item.diet_label && (
                                <Badge 
                                  className="text-xs text-white border-0" 
                                  style={{ background: getDietColor(item.diet_type) }}
                                >
                                  {item.diet_label}
                                </Badge>
                              )}
                              {item.meal_name ? (
                                <>{item.meal_name} - Opción {item.option_number}</>
                              ) : (
                                <>Recetas del {new Date(item.created_at).toLocaleDateString('es-ES', {
                                  day: 'numeric',
                                  month: 'long'
                                })}</>
                              )}
                            </h3>
                            <p className="text-sm text-gray-400 flex items-center gap-2 mt-1">
                              <FaClock style={{ color: '#00F0FF' }} />
                              {new Date(item.created_at).toLocaleTimeString('es-ES', {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                            {item.ingredients && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {item.ingredients.slice(0, 5).map((ing, i) => (
                                  <Badge key={i} className="text-xs bg-white/10">{ing}</Badge>
                                ))}
                                {item.ingredients.length > 5 && (
                                  <Badge className="text-xs bg-white/5">+{item.ingredients.length - 5} más</Badge>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      {item.recipes_text && (
                        <div className="p-6">
                          <div className="prose prose-invert prose-sm max-w-none">
                            <ReactMarkdown
                              components={{
                                h3: ({ children }) => (
                                  <h3 className="text-lg font-bold mt-4 mb-2" style={{ color: 'var(--brand-primary)' }}>
                                    {children}
                                  </h3>
                                )
                              }}
                            >
                              {item.recipes_text}
                            </ReactMarkdown>
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div 
                    className="rounded-2xl p-12 text-center"
                    style={{ 
                      background: 'rgba(255, 255, 255, 0.03)',
                      border: '2px dashed rgba(255, 255, 255, 0.1)'
                    }}
                  >
                    <FaHistory className="text-5xl mx-auto mb-4" style={{ color: 'var(--brand-primary)' }} />
                    <h3 className="text-xl font-semibold text-white mb-2">
                      Sin historial de recetas
                    </h3>
                    <p className="text-gray-500 mb-6">
                      Genera tus primeras recetas para verlas aquí
                    </p>
                    <Button
                      onClick={() => setActiveTab('generate')}
                      className="rounded-full px-8 font-bold uppercase tracking-wider text-white"
                      style={{ background: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)' }}
                    >
                      Generar Recetas
                    </Button>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default RecipeIdeas;
